import { FC, useEffect } from 'react';
import { useStore } from 'base/store';
import { useRouter } from 'next/router';
import { setCookie } from 'ava-util/dist/cookie';
import { generatePath } from 'ava-util/dist/request';
import { COOKIE_COUNTRY, COOKIE_CURRENCY, COOKIE_LANG, COOKIE_LOCALE } from 'base/config/constant';
import { HeaderBarMenuProps } from '@app/common/PageHeader/HeaderBarDropdown';
import { ILocaleList, ICountry, ILocale } from 'mod/store/locale/interface';
import { IconFlag } from '@base/icon';
import { Form, Button, Select } from 'antd';
import styles from './index.module.less';
import { useTrans } from 'base/utils/i18n';

type MenuProps = HeaderBarMenuProps & {
    locale: ILocale;
    list: ILocaleList;
}

export const Menu: FC<MenuProps> = ({ onClose, locale, list }) => {
    const router = useRouter();
    const { rootStore } = useStore();
    const [form] = Form.useForm();
    const { t } = useTrans();

    const changeCountry = (countryCode: string) => {
        const countryItem = list.find(item => item.countryCode === countryCode) as ICountry;
        if (countryItem) {
            form.setFieldsValue({
                currency: countryItem.currencyList.find(item => item.defaultValue)?.code,
                lang: countryItem.languageList.find(item => item.defaultValue)?.code,
            })
        }
    }

    const handleSubmit = (values) => {
        onClose?.();
        if (values.country !== locale.country || values.currency !== locale.currency || values.lang !== locale.lang) {
            rootStore.update(values)
            const locale = values.lang === 'en' && values.country === 'US' ? '' : `${values.lang}-${values.country}`;
            setCookie(COOKIE_LOCALE, locale);
            setCookie(COOKIE_COUNTRY, values.country);
            setCookie(COOKIE_CURRENCY, values.currency);
            setCookie(COOKIE_LANG, values.lang);
            window.location.href = generatePath(`${locale ? ('/' + locale) : ''}${router.asPath}`, { language: void 0, country: void 0, currency: void 0 });
        }
    }

    useEffect(() => {
        form.setFieldsValue(locale)
    }, []);

    return (
        <Form<ILocale>
            className={styles.form}
            form={form}
            name="locale"
            layout="vertical"
            requiredMark={false}
            onFinish={handleSubmit}
        >
            <Form.Item
                label={t('web_pc_locale_lable_country')}
                name="country"
            >
                <Select
                    popupClassName={styles.options}
                    onChange={e => changeCountry(e)}
                    options={list.map(item => ({
                        value: item.countryCode,
                        label: <><IconFlag code={item.countryCode} size={26}/>{item.countryName}</>,
                    }))}
                    showArrow
                />
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(preValues, curValues) => preValues.country !== curValues.country}
            >

                {({ getFieldValue }) => {
                    const countryCode = getFieldValue('country');
                    const countryItem = list.find(item => item.countryCode === countryCode) as ICountry;
                    const { languageList, currencyList } = countryItem || {};
                    return !countryItem ? null : (
                        <>
                            <Form.Item
                                label={t('web_locale_lable_language')}
                                name="lang"
                            >
                                <Select
                                    popupClassName={styles.options}
                                    options={languageList.map(item => ({
                                        value: item.code,
                                        label: item.name,
                                    }))}
                                    showArrow={languageList.length > 1}
                                    disabled={languageList.length === 1}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t('web_locale_lable_currency')}
                                name="currency"
                            >
                                <Select
                                    popupClassName={styles.options}
                                    options={currencyList.map(item => ({
                                        value: item.code,
                                        label: `${item.code} (${item.currencySymbol})`
                                    }))}
                                    showArrow={currencyList.length > 1}
                                    disabled={currencyList.length === 1}
                                />
                            </Form.Item>
                        </>
                    )
                }}
            </Form.Item>

            <Form.Item noStyle>
                <Button type={"primary"} block htmlType={'submit'}>{t('web_common_btn_save')}</Button>
            </Form.Item>
        </Form>
    )
};